import React from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';

import { connect } from 'react-redux';
import { isAfter } from 'date-fns';

import Modal from '../../shared/Modal';
import Rating from '../../containers/campaigns/components/UserFeedback/Rating.jsx';
import { CreateAccountModal } from '../../shared/SignIn/CreateAccountModal.jsx';
import { Dialog } from '@radix-ui/themes';

import {
  updateTileData,
  addCompletionAndPointsToProgressBar,
  setTilesData,
  setTilesState,
} from '../redux/tileStateManagerActions';
import { getSanitizedState } from '../redux/selectors';
import { Fetcher } from '../helpers';
import { api } from '../../lib/api';
import FullSizeTile from '../../shared/FullSizeTile';
import { FullSizeTileNavigationWrapper } from '../../shared/FullSizeTile/FullSizeTileNavigationWrapper';

const Container = styled.div`
  pointer-events: ${props => (props.loading || props.disable ? 'none' : '')};
`;

const pingView = id => {
  Fetcher.xmlHttpRequest({
    method: 'POST',
    path: `/api/v1/tiles/${id}/mark_as_viewed`,
    success: () => null,
  });
};

const resetPriorSelections = () => {
  const tileQuizSection = document.getElementsByClassName(
    'multiple-choice-answer',
  );

  const freeResponse = document.getElementById('free_form_response');

  if (tileQuizSection.length) {
    for (let i = 0; i < tileQuizSection.length; i++) {
      tileQuizSection[i].className = 'multiple-choice-answer';
      tileQuizSection[i].style.pointerEvents = '';

      if (tileQuizSection[i].nextElementSibling) {
        tileQuizSection[i].nextElementSibling.className = 'answer_target';
      }
    }
  }
  if (freeResponse) {
    freeResponse.value = '';
  }
};

const updateUrlWithTileId = tileId => {
  const url = new URL(window.location);
  url.searchParams.set('tile_id', tileId);
  window.history.replaceState({}, '', url.toString());
};

class TileStateManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tile: {},
      currentTileIndex: null,
      loading: true,
      disable: false,
      openCreateAccountModal: false,
    };
    this.populateNewTileContentByIndex = this.populateNewTileContentByIndex.bind(
      this,
    );
    this.submitAnswer = this.submitAnswer.bind(this);
    this.trackLinkClick = this.trackLinkClick.bind(this);
    this.firstIncompleteTile = this.firstIncompleteTile.bind(this);
    this.tileToShow = this.tileToShow.bind(this);
    this.timer = this.setTimer.bind(this);
    this.handleReturnToCampaign = this.handleReturnToCampaign.bind(this);

    this.arrowRightRef = React.createRef();
    this.arrowLeftRef = React.createRef();
  }

  static revertSelection() {
    const element = document.querySelector('div.multiple_choice_group');
    const hiddenElement = document.querySelector('[id*="form_hidden"]');

    if (element) {
      const options = element.children;

      for (let i = 0; i < options.length; i++) {
        options[i].style.display = 'block';
      }
    }

    if (hiddenElement) hiddenElement.style.display = 'none';
  }

  componentDidMount() {
    const { tileOrigin, originId } = this.props;

    this.setCurrentTileIndex(originId, tileOrigin);
    this.renderTileFullTileData(originId);
  }

  componentDidUpdate(prevProps) {
    const { tileOrigin, originId } = this.props;

    // If tileOrigin or originId changed, we need to update the tile display
    if (
      prevProps.tileOrigin !== tileOrigin ||
      prevProps.originId !== originId
    ) {
      this.setCurrentTileIndex(originId, tileOrigin);
      this.renderTileFullTileData(originId);
    }
  }

  fetchFullSizeTileData(id, origin, pingTileView) {
    const shouldTranslate = origin !== 'explore';
    Fetcher.xmlHttpRequest({
      method: 'GET',
      path: `/api/v1/tiles/${id}?translate=${shouldTranslate}&ping_tile_view=${pingTileView}&include_completion=${origin ===
        'complete'}`,
      success: resp => {
        this.props.updateTileData({ id, origin, resp });
        this.setState({ tile: resp });
        this.setState({ loading: false });
      },
    });
  }

  renderTileFullTileData(id) {
    const { tiles, tileOrigin } = this.props;
    const pingTileView =
      tileOrigin === 'complete' || tileOrigin === 'incomplete'
        ? 'true'
        : 'false';
    if (!this.state.loading) {
      this.setState({ loading: true });
    }
    if (tiles[tileOrigin][id].fullyLoaded) {
      if (tileOrigin === 'incomplete') {
        resetPriorSelections();
      }
      if (pingTileView === 'true') {
        pingView(id);
      }
      this.setState({ loading: false });
      this.setState({ tile: tiles[tileOrigin][id] });
    } else {
      this.fetchFullSizeTileData(id, tileOrigin, pingTileView);
    }
  }

  calculateRolloverIndex(differenceIndex) {
    const { tiles, tileOrigin } = this.props;

    if (
      differenceIndex >= tiles[tileOrigin].count &&
      tiles.paginateState.allTilesDisplayed
    ) {
      // If we're at the end of completed tiles and there are incomplete tiles available,
      // switch to the first incomplete tile instead of rolling over to the first completed tile
      if (
        tileOrigin === 'complete' &&
        tiles.incomplete &&
        tiles.incomplete.order.length > 0
      ) {
        return 'switchToIncomplete';
      }
      return 0;
    } else if (differenceIndex < 0) {
      return tiles[tileOrigin].count - 1;
    }
    return differenceIndex;
  }

  populateNewTileContentByIndex(
    indexDifference,
    nextAfterCompletion,
    nextButtonClick = false,
  ) {
    if (!this.state.loading || nextAfterCompletion) {
      window.scrollTo(0, 0);
      const { tiles, tileOrigin, originId } = this.props;
      const newIndex = this.calculateRolloverIndex(
        this.state.currentTileIndex + indexDifference,
      );

      this.setState({ loading: true });

      // Handle switching to incomplete tiles when at the end of completed tiles
      if (newIndex === 'switchToIncomplete') {
        const firstIncompleteTileId = tiles.incomplete.order[0];

        // Use the callback to switch to incomplete tiles
        this.props.switchToTileOrigin('incomplete', firstIncompleteTileId);

        clearTimeout(this.timer);
        return;
      }

      if (newIndex >= tiles[tileOrigin].order.length) {
        const paginateState =
          tileOrigin === 'incomplete' && !nextButtonClick
            ? { incompleteTilesPage: 1 }
            : {};

        this.props.loadTiles({
          perPage: 16,
          paginateState,
          campaignUuid: tiles[tileOrigin][originId].campaignUuid,
          success: resp => {
            this.props.addTiles(resp.tiles);

            setTimeout(() => {
              const tileId = this.props.tiles[tileOrigin].order[newIndex]
                ? this.props.tiles[tileOrigin].order[newIndex]
                : this.props.tiles[tileOrigin].order[0];

              this.renderTileFullTileData(tileId);
              this.setState({ currentTileIndex: newIndex });
              updateUrlWithTileId(tileId);
            }, 0);
          },

          error: err => console.error(err),
        });
      } else {
        this.constructor.revertSelection();
        const tileId = tiles[tileOrigin].order[newIndex];
        this.renderTileFullTileData(tileId);
        this.setState({ currentTileIndex: newIndex });
        updateUrlWithTileId(tileId);
      }
    }
    clearTimeout(this.timer);
  }

  setCurrentTileIndex(id, tileOrigin) {
    const currentTileIndex = this.props.tiles[tileOrigin].order.indexOf(id);
    this.setState({ currentTileIndex });
  }

  setTimer() {
    this.timer = setTimeout(() => {
      this.setState({ disable: false });
      setTimeout(() => {
        this.populateNewTileContentByIndex(1, true);
      }, 500);
    }, 500);
  }

  submitAnswer(id, answerIndex, freeFormResponse, correctAnswer) {
    const origin = this.props.tileOrigin;
    const tile = this.props.tiles[origin][id];

    if (tile.status === 'plan') {
      return;
    }

    if (correctAnswer === false) {
      api({
        method: 'post',
        url: '/api/wrong_tile_completions',
        data: {
          wrong_tile_completion: {
            tile_id: id,
            answer_index: answerIndex,
          },
        },
      });
    } else {
      setTimeout(() => {
        if (origin === 'explore') {
          this.setTimer();
        } else {
          Fetcher.xmlHttpRequest({
            method: 'POST',
            path: `/api/tile_completions`,
            params: {
              tile_id: id,
              answer_index: answerIndex,
              free_form_response: freeFormResponse,
            },
            success: () => {
              const ratingsCreationDate = new Date(
                import.meta.env.VITE_RATINGS_CREATION_DATE,
              );
              const campaignCreationDate = new Date(tile.campaignCreatedAt);
              const {
                progressBarData,
                demo,
                userData,
                organization,
              } = this.props;
              this.props.updateTileData({
                origin,
                id,
                resp: { answerIndex, freeFormResponse, complete: true },
              });
              this.props.addCompletionAndPointsToProgressBar({
                points: tile.points,
                completion: 1,
              });
              if (
                progressBarData &&
                progressBarData.completedTiles + 1 ===
                  progressBarData.incompletedTiles
              ) {
                let uri = `/board/${demo.publicSlug}`;

                if (tile.campaignSlug) {
                  uri = `${uri}/${tile.campaignSlug}`;
                }

                this.props.setTilesState({});

                if (
                  demo.newAirbo &&
                  isAfter(campaignCreationDate, ratingsCreationDate) &&
                  !userData.campaignFeedbacksIds.includes(tile.campaignUuid) &&
                  !userData.isGuestUser
                ) {
                  this.props.navigateTo(uri);

                  Modal({
                    component: <Rating campaignUuid={tile.campaignUuid} />,
                    width: '500px',
                    padding: '25px',
                  });
                } else if (
                  userData.isGuestUser &&
                  this.props.shouldConvertGuestAsUser
                ) {
                  this.setState({ openCreateAccountModal: true });
                } else {
                  this.props.navigateTo(uri, {
                    flash: {
                      success: true,
                      title: 'Congratulations!',
                      child: `You've finished all new ${organization.tilesWording}!`,
                      event: 'allTilesCompleted',
                      confirmBtnCssClass: 'primary-blue-button',
                    },
                  });
                }
              } else {
                this.populateNewTileContentByIndex(1, true);
              }
              // Patch sign up modal to communicate with React for every 2nd tile answered
              if (document.getElementById('guest-conversion-modal')) {
                window.Airbo.PubSub.publish('tileAnswered');
              }
            },
            err: () => {
              Swal.fire({
                icon: 'error',
                title: 'We could not save your answer',
                text:
                  'Something went wrong saving this answer. Please reload the page and try again.',
                confirmButtonText: 'Reload',
              }).then(() => document.location.reload());
            },
          });
        }
      }, 500);
    }
  }

  trackLinkClick(target, tileId) {
    const clickedLink = target.getAttribute('href');
    if (this.props.tileOrigin !== 'explore') {
      Fetcher.xmlHttpRequest({
        method: 'POST',
        path: `/api/tiles/${tileId}/tile_link_trackings`,
        params: { clicked_link: clickedLink },
        success: () => {},
      });
    }
  }

  tileToShow() {
    const { tiles, tileOrigin } = this.props;
    const currentTileId = tiles[tileOrigin].order[this.state.currentTileIndex];

    return tiles[tileOrigin][currentTileId];
  }

  firstIncompleteTile() {
    const { tiles } = this.props;
    const incompleteTiles = tiles.incomplete;
    const firstTileId = incompleteTiles.order[0];

    this.setState({ currentTileIndex: 0 });

    return incompleteTiles[firstTileId];
  }

  handleReturnToCampaign() {
    const { demo } = this.props;
    const { tile } = this.state;

    if (tile.campaignSlug && demo?.publicSlug) {
      const uri = `/board/${demo.publicSlug}/${tile.campaignSlug}`;
      this.props.navigateTo(uri);
    }
  }

  render() {
    if (!this.state.tile.id) return null;

    return (
      <Container $loading={this.state.loading} disable={this.state.disable}>
        <FullSizeTileNavigationWrapper
          arrowLeftRef={this.arrowLeftRef}
          arrowRightRef={this.arrowRightRef}
        >
          <Dialog.Root
            open={this.state.openCreateAccountModal}
            onOpenChange={open =>
              this.setState({ openCreateAccountModal: open })
            }
          >
            <CreateAccountModal
              redirectParam={{ tileId: this.state.tile.id }}
              showSignInOption={true}
            />
          </Dialog.Root>
          <FullSizeTile
            tile={this.state.tile}
            organization={this.props.organization}
            demo={this.props.demo}
            loading={this.state.loading}
            nextTile={() => this.populateNewTileContentByIndex(1, null, true)}
            prevTile={() => this.populateNewTileContentByIndex(-1)}
            closeTile={this.props.closeTile}
            tileOrigin={this.props.tileOrigin}
            userData={this.props.userData}
            tileActions={this.props.tileActions}
            submitAnswer={this.submitAnswer}
            trackLinkClick={this.trackLinkClick}
            shouldConvertGuestAsUser={this.props.shouldConvertGuestAsUser}
            arrowRightRef={this.arrowRightRef}
            arrowLeftRef={this.arrowLeftRef}
            onReturnToCampaign={this.handleReturnToCampaign}
          />
        </FullSizeTileNavigationWrapper>
      </Container>
    );
  }
}

export default connect(getSanitizedState, {
  updateTileData,
  addCompletionAndPointsToProgressBar,
  setTilesData,
  setTilesState,
})(TileStateManager);
